/* eslint-disable @stripe-internal/embedded/no-restricted-globals */
import type {FetchApiError} from '@stripe-internal/connect-embedded-lib';
import {setup, getDefaultConnector} from './connect/connector';
import {markConnectTiming} from './utils/telemetry/performanceMetrics';
import {livemodeFromPublishableKey} from './utils/livemodeFromPublishableKey';
import type {
  InitOptions,
  UpdateOptions,
} from './connect/ConnectJSInterface/InitAndUpdateOptionsTypes';
import {validateInitOptions} from './connect/ConnectJSInterface/validateOptions';
import {withErrorReportingAndAnalytics} from './data-layer-client/ErrorReportingAndAnalytics';
import {transformInitOptions} from './connect/ConnectJSInterface/transformOptions';
import {preloadAccessoryLayerFrame} from './ui-layer-frame/accessory-layer-frame/preloadAccessoryLayerFrame';
import {requestIdleCallbackPonyfill} from './vendor/requestIdleCallback';
import {createApiPreconnectLink} from './utils/createApiPreconnectLink';
import {preloadDataLayer} from './data-layer-client/buildDataLayerFrame';
import {isWindowStripeConnectDefinedCorrectly} from './utils/isWindowStripeConnectDefinedCorrectly';
import {setLastSeenConnectJsInitializationPattern} from './utils/telemetry/connectInitializationPatternTracker';
import {computeCurrentScriptUrlContext} from './utils/getCurrentScriptUrlContext';

export type StripeConnect = {
  onLoad?: () => void;
  init?: (options?: InitOptions) => void;
  update?: (options?: UpdateOptions) => void;
  logout?: () => void;
  optimizedLoading?: boolean;
};
declare global {
  interface Window {
    StripeConnect: undefined | StripeConnect;
  }
}

export type ConnectJSInitializationPattern =
  | 'callInitDirectly'
  | 'onLoadBeforeConnectJSLoad'
  | 'onLoadAfterConnectJSLoad';

/**
 * This function sets up the Stripe Connect global object
 */
export function setupStripeConnect() {
  computeCurrentScriptUrlContext(document.currentScript);

  const link = createApiPreconnectLink(document);
  document.head.appendChild(link);

  // Get the onLoad function from the window object if it exists and is defined as a function
  // We obtain it here as a best effort, as the next step may clear the StripeConnect global
  const onLoadFunction =
    typeof window.StripeConnect?.onLoad === 'function'
      ? window.StripeConnect?.onLoad
      : undefined;

  // Initialize the StripeConnect global in the case that is was not yet defined, or it was defined with anything unexpected
  if (!isWindowStripeConnectDefinedCorrectly(window.StripeConnect)) {
    window.StripeConnect = {};

    // Preserve the presence of the onLoad function if it was defined
    if (onLoadFunction) {
      window.StripeConnect.onLoad = onLoadFunction;
    }
  }

  // Don't define Stripe connect if it was already defined, which would indicate that connect.js was already loaded
  if (
    window.StripeConnect.init &&
    typeof window.StripeConnect.init === 'function'
  ) {
    return;
  }

  // Note: we don't do this with requestIdleCallbackPonyfill, since we want the data layer to be loaded ASAP
  if (document.body) {
    preloadDataLayer();
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      preloadDataLayer();
    });
  }

  // Preload the accessory layer frame
  requestIdleCallbackPonyfill(preloadAccessoryLayerFrame);

  Object.defineProperty(window.StripeConnect, 'init', {
    value: (options: unknown) => {
      markConnectTiming('initStart');

      const transformedOptions = transformInitOptions(options);
      validateInitOptions(
        transformedOptions,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        livemodeFromPublishableKey((options as any)?.publishableKey, true),
        false,
      );

      const {
        clientSecret,
        publishableKey,
        appearance,
        metaOptions,
        locale,
        refreshClientSecret,
        fetchClientSecret,
        fonts,
        onRiskSignalsCollection,
        displayOptions,
      } = transformedOptions;

      return setup(
        publishableKey,
        clientSecret,
        fetchClientSecret,
        metaOptions,
        appearance,
        locale,
        refreshClientSecret,
        fonts,
        onRiskSignalsCollection,
        displayOptions,
      );
    },
  });

  Object.defineProperty(window.StripeConnect, 'update', {
    value: (options: unknown) => {
      const connector = getDefaultConnector();
      connector.update(transformInitOptions(options));
    },
  });

  Object.defineProperty(window.StripeConnect, 'logout', {
    value: async () => {
      const connector = getDefaultConnector();
      try {
        await withErrorReportingAndAnalytics(
          null,
          connector.connectJsOptions.getValues().metaOptions,
          connector.analytics,
          'submerchant_surfaces_logout',
          connector.frameMessenger.logout,
          false,
          undefined,
          undefined,
          undefined,
          ['invalid_session', 'reauth_required'],
        );
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const status = (error as FetchApiError).error.httpStatus;
        if (status !== 401 && status !== 404) {
          throw new Error('login unsuccessful');
        }
      }
    },
  });

  markConnectTiming('connectGlobalReady');
  // If `onLoad` is defined, call it. If it's not defined yet, call it if it gets set later.
  if (onLoadFunction) {
    // This is the case where the user sets `onLoad` before the connect.js script has loaded. In this case
    // we call the onLoad function (now that connect.js is loading)
    setLastSeenConnectJsInitializationPattern('onLoadBeforeConnectJSLoad');
    onLoadFunction();
  } else {
    Object.defineProperty(window.StripeConnect, 'onLoad', {
      set(onLoad: () => void) {
        if (typeof onLoad === 'function') {
          // This is the case where the user sets `onLoad` after the connect.js script has loaded
          // In this case, we immediately call the onLoad function
          setLastSeenConnectJsInitializationPattern('onLoadAfterConnectJSLoad');
          onLoad();
        }
      },
    });
  }
}
